import type { ReactElement } from 'react';
import { Unlink, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { FederatedIdentityViewModel } from '../api';

/** Default global-plane target for the linked-user link; overridable so a tenant page can reuse this. */
function defaultUserHref(userGuid: string): string {
  return `/platform/users/${encodeURIComponent(userGuid)}`;
}

export function FederatedIdentitiesTable({
  rows,
  onLink,
  onUnlink,
  pendingUnlinkGuid,
  userHref = defaultUserHref,
}: {
  readonly rows: readonly FederatedIdentityViewModel[];
  readonly onLink: (row: FederatedIdentityViewModel) => void;
  readonly onUnlink: (row: FederatedIdentityViewModel) => void;
  readonly pendingUnlinkGuid: string | null;
  /**
   * Builds the linked-user detail href from a user guid. Defaults to the global-plane user page;
   * return `null` to render the linked user as plain text (no clickable profile) — the tenant console
   * has no per-user profile page, so it opts out of the link (PTV2-1472).
   */
  readonly userHref?: (userGuid: string) => string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.platform.federatedIdentities.table';

  return (
    <DataTableTable aria-label={t('routes.platform.federatedIdentities.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.identity`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[12rem]">
            {t(`${col}.linkedUser`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.provider`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[14rem]">
            {t(`${col}.subject`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[7rem]">
            {t(`${col}.status`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[11rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <IdentityRow
            key={row.guid}
            row={row}
            onLink={onLink}
            onUnlink={onUnlink}
            pendingUnlinkGuid={pendingUnlinkGuid}
            userHref={userHref}
          />
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

function IdentityRow({
  row,
  onLink,
  onUnlink,
  pendingUnlinkGuid,
  userHref,
}: {
  readonly row: FederatedIdentityViewModel;
  readonly onLink: (row: FederatedIdentityViewModel) => void;
  readonly onUnlink: (row: FederatedIdentityViewModel) => void;
  readonly pendingUnlinkGuid: string | null;
  readonly userHref: (userGuid: string) => string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.platform.federatedIdentities.table';
  const isPending = row.status === 'PENDING';
  const isRetired = row.status === 'RETIRED';
  // The captured name/email are display hints for an otherwise opaque subject; fall back to the subject.
  const primary = row.capturedName.length > 0 ? row.capturedName : t(`${col}.unknownIdentity`);
  // Always show something human-readable; the guid stays the link target (and a hover tooltip when
  // the name can't be resolved, e.g. a deleted user).
  const linkedLabel = row.userName.length > 0 ? row.userName : t(`${col}.unknownUser`);
  // A null href means "no profile page for this plane" — render the name as plain text.
  const href = row.userGuid.length > 0 ? userHref(row.userGuid) : null;

  return (
    <DataTableTr className="even:bg-muted/55">
      <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
        <span className="flex flex-col leading-tight">
          <span className="truncate">{primary}</span>
          {row.capturedEmail.length > 0 && (
            <span className="text-xs text-muted-foreground truncate">{row.capturedEmail}</span>
          )}
        </span>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {row.userGuid.length === 0 ? (
          <span className="text-muted-foreground">{t(`${col}.notLinked`)}</span>
        ) : href !== null ? (
          <Link
            to={href}
            title={row.userName.length > 0 ? row.userName : row.userGuid}
            className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 truncate inline-block max-w-full"
          >
            {linkedLabel}
          </Link>
        ) : (
          <span
            className="truncate inline-block max-w-full text-foreground/90"
            title={row.userName.length > 0 ? row.userName : row.userGuid}
          >
            {linkedLabel}
          </span>
        )}
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
        <span className="block truncate">{row.providerIssuer}</span>
      </DataTableTd>
      <DataTableTd
        className="h-[40px] py-0 font-mono text-xs text-muted-foreground"
        dataOffset={false}
      >
        <span className="block truncate" title={row.externalSubject}>
          {row.externalSubject}
        </span>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        <Badge variant={isPending ? 'default' : 'outline'}>
          {t(`routes.platform.federatedIdentities.statusLabel.${row.status}`)}
        </Badge>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {isRetired ? (
          <span className="text-xs text-muted-foreground">{t(`${col}.noActions`)}</span>
        ) : (
          <div className="flex items-center gap-1">
            {isPending && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onLink(row);
                }}
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                {t(`${col}.linkAction`)}
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              aria-label={isPending ? t(`${col}.dismissAction`) : t(`${col}.unlinkAction`)}
              title={isPending ? t(`${col}.dismissAction`) : t(`${col}.unlinkAction`)}
              loading={pendingUnlinkGuid === row.guid}
              onClick={() => {
                onUnlink(row);
              }}
            >
              <Unlink className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </DataTableTd>
    </DataTableTr>
  );
}
